import { Component, Input, OnInit } from '@angular/core';

import { combineLatest, EMPTY, Observable } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';

import { ModalButtons } from '@fcom/ui-components';
import { asPaxTypeKey } from '@fcom/dapi/utils';
import { SentryLogger } from '@fcom/core/services';
import { Amount, PriceService } from '@fcom/dapi';
import {
  FinnairCharge,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairPrice,
  FinnairServiceCategoryPriceItem,
  FinnairTotalPricesDetails,
  FinnairAmount,
} from '@fcom/dapi/api/models';
import { safeMap as safeMapObs } from '@fcom/rx';
import { isPresent } from '@fcom/core/utils';
import { hasUnitPrices } from '@fcom/common/utils';

interface BreakdownModel extends FinnairPassengerItem {
  prices: FinnairPrice;
  services: PriceService[];
  total: FinnairAmount;
}

interface TaxModalModel {
  taxes: FinnairCharge[];
  ptc: string;
  total: Amount;
}

const hasPassengers = (passengers: FinnairPassengerItem[]): boolean => isPresent(passengers) && passengers.length > 0;

@Component({
  selector: 'fin-price-breakdown',
  templateUrl: './price-breakdown.component.html',
})
export class PriceBreakdownComponent implements OnInit {
  public readonly ModalButtons = ModalButtons;
  public readonly asPaxTypeKey = asPaxTypeKey;
  public readonly FinnairPassengerCode = FinnairPassengerCode;

  @Input()
  prices$: Observable<FinnairTotalPricesDetails>;
  @Input()
  passengers$: Observable<FinnairPassengerItem[]>;
  @Input()
  showBaseFare = true;
  @Input()
  showTotalTax = true;
  @Input()
  showPoints = false;
  @Input()
  expandOpen = false;

  breakdowns$: Observable<BreakdownModel[]>;
  totalPrice$: Observable<Amount>;
  showSubTotals$: Observable<boolean>;
  taxModalOpen = false;
  taxModalData: TaxModalModel;

  constructor(private sentryLogger: SentryLogger) {}

  ngOnInit(): void {
    this.breakdowns$ = combineLatest([this.passengers$, this.prices$]).pipe(
      filter(([passengers, prices]) => hasPassengers(passengers) && hasUnitPrices(prices)),
      map(([passengers, prices]) =>
        passengers
          .filter((passenger) => parseFloat(prices.total.totalPerPax?.[passenger.id]?.totalAmount.amount) > 0)
          .map((passenger: FinnairPassengerItem) => ({
            ...passenger,
            prices: prices.flight?.totalPerPax?.[passenger.id] || prices.services?.totalPerPax?.[passenger.id],
            services: this.getServicesForPax(prices.services?.totalPerCategory, passenger),
            total: prices.total.totalPerPax?.[passenger.id]?.totalAmount,
          }))
      ),
      catchError((e: unknown) => {
        this.sentryLogger.error('Exception in handling prices of passengers', e);
        return EMPTY;
      })
    );

    this.totalPrice$ = this.prices$.pipe(safeMapObs((prices) => ({ ...prices.total?.total?.totalAmount })));
    this.showSubTotals$ = this.passengers$.pipe(
      safeMapObs((passengers: FinnairPassengerItem[]) => passengers.length > 1)
    );
  }

  openTaxModal(event, taxes: FinnairCharge[], ptc: string, total: Amount) {
    event.stopPropagation();

    this.taxModalOpen = true;
    this.taxModalData = {
      taxes,
      ptc,
      total,
    };
  }

  closeTaxModal() {
    this.taxModalData = null;
  }

  getServicesForPax(
    pricesForCategory: FinnairServiceCategoryPriceItem[] = [],
    passenger: FinnairPassengerItem
  ): PriceService[] {
    const sortOrder: string[] = ['seat', 'baggage', 'meal', 'wifi', 'travelComfort', 'lounge', 'cover'].reverse();
    return pricesForCategory
      .map((c) => {
        const name = `${c.category}Count`;
        const count = c.totalPerPax[passenger.id]?.quantity ?? 0;
        const totalAmount = c.totalPerPax[passenger.id]?.price.totalAmount;
        const originalTotalAmount = c.totalPerPax[passenger.id]?.price.originalTotalAmount;
        return {
          id: c.category,
          total: totalAmount ?? null,
          originalTotal: originalTotalAmount ?? null,
          count,
          key: `purchase.payment.${name}`,
          [name]: count,
        };
      })
      .filter((service) => service.count > 0)
      .sort((a, b) => sortOrder.indexOf(b.id) - sortOrder.indexOf(a.id));
  }
}
